//angular
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { Routes, UrlMatchResult, UrlSegment } from '@angular/router';

export enum AuthRouteIntent {
    ProvidedTenant = 'provided-tenant',
    MissingTenant = 'missing-tenant',
}

export interface AuthRouteState {
    intent: AuthRouteIntent;
    tenant?: string;
}

export const APP_CORE_ROUTE_PATHS = new InjectionToken<ReadonlySet<string>>('APP_CORE_ROUTE_PATHS');
export const APP_TECHNICAL_NON_TENANT_ROUTES = new InjectionToken<ReadonlySet<string>>('APP_TECHNICAL_NON_TENANT_ROUTES');

@Injectable({
    providedIn: 'root'
})
export class AuthRouteHelper {
    private readonly coreRoutePaths: ReadonlySet<string>;
    private readonly technicalNonTenantRoutes: ReadonlySet<string>;

    constructor(
        @Optional() @Inject(APP_CORE_ROUTE_PATHS) coreRoutePaths?: ReadonlySet<string>,
        @Optional() @Inject(APP_TECHNICAL_NON_TENANT_ROUTES) technicalNonTenantRoutes?: ReadonlySet<string>,
    ) {
        this.coreRoutePaths = new Set(Array.from(coreRoutePaths || []).map(path => path.toLowerCase()));
        this.technicalNonTenantRoutes = new Set(Array.from(technicalNonTenantRoutes || []).map(path => path.toLowerCase()));
    }

    public static extractCanonicalCoreRoutePaths(routeConfig: Routes): ReadonlySet<string> {
        const paths = routeConfig
            .filter(route => !!route.path && route.path !== '**' && !route.redirectTo)
            .map(route => (route.path as string).toLowerCase());

        return new Set(paths);
    }

    public isTenantEnvelope(routeSegments: string[]): boolean {

        const firstSegment = routeSegments[0].toLowerCase();            

        if (this.technicalNonTenantRoutes.has(firstSegment)) {
            return false;
        }

        const firstIsCoreRoute = this.coreRoutePaths.has(firstSegment);      
        
        if (routeSegments.length < 2) {
            return !firstIsCoreRoute;
        }
            
        const secondSegment = routeSegments[1].toLowerCase();
        
        const secondIsCoreRoute = this.coreRoutePaths.has(secondSegment);
        
        if (!firstIsCoreRoute) {
            return true;
        }

        //if tenant name just so happens to match a core route,
        //then the second path has to be a core route
        return secondIsCoreRoute;
    }

    public resolveRouteState(pathname: string): AuthRouteState {
        const normalizedPath = (pathname || '/').split('?')[0].split('#')[0].trim();
        const routeSegments = normalizedPath
            .split('/')
            .map(segment => segment.trim())
            .filter(segment => segment.length > 0)
            .map(segment => decodeURIComponent(segment));

        if (routeSegments.length === 0) {
            return { intent: AuthRouteIntent.MissingTenant };
        }

        if (!this.isTenantEnvelope(routeSegments)) {
            return { intent: AuthRouteIntent.MissingTenant };
        }

        return {
            intent: AuthRouteIntent.ProvidedTenant,
            tenant: routeSegments[0],
        };
    }

    public isTechnicalNonTenantPath(pathname: string): boolean {
        const routeSegments = this.extractRouteSegments(pathname);
        if (routeSegments.length === 0) {
            return false;
        }

        return this.technicalNonTenantRoutes.has(routeSegments[0].toLowerCase());
    }

    public isAuthCallbackPath(pathname: string): boolean {
        const routeSegments = this.extractRouteSegments(pathname);
        if (routeSegments.length === 0) {
            return false;
        }

        return routeSegments[0].trim().toLowerCase() === 'auth';
    }

    public ensureTenantInUrl(urlPath: string, tenant: string, fallbackCoreRoute: string = 'landing'): string {
        const normalizedUrlPath = (urlPath || '/').trim();
        const splitIndex = this.findSuffixStart(normalizedUrlPath);
        const pathname = splitIndex >= 0 ? normalizedUrlPath.substring(0, splitIndex) : normalizedUrlPath;
        const suffix = splitIndex >= 0 ? normalizedUrlPath.substring(splitIndex) : '';

        const routeState = this.resolveRouteState(pathname);
        if (routeState.intent === AuthRouteIntent.ProvidedTenant) {
            const routeSegments = this.extractRouteSegments(pathname);
            const currentTenant = routeSegments[0];
            if (currentTenant === tenant) {
                if (routeSegments.length === 1) {
                    return `/${encodeURIComponent(tenant)}/${fallbackCoreRoute}${suffix}`;
                }

                return this.toAbsolutePath(pathname) + suffix;
            }

            const currentTenantLooksLikeCoreRoute = this.coreRoutePaths.has(currentTenant.toLowerCase());
            const secondSegmentLooksLikeCoreRoute = routeSegments.length > 1 && this.coreRoutePaths.has(routeSegments[1].toLowerCase());
            if (currentTenantLooksLikeCoreRoute && secondSegmentLooksLikeCoreRoute) {
                return `/${encodeURIComponent(tenant)}/${routeSegments.join('/')}${suffix}`;
            }

            routeSegments[0] = encodeURIComponent(tenant);
            return `/${routeSegments.join('/')}${suffix}`;
        }

        const routeSegments = this.extractRouteSegments(pathname);
        const pathIsOnlyAuthenticatedTenant = routeSegments.length === 1
            && routeSegments[0].toLowerCase() === tenant.toLowerCase();
        const pathWithoutTenant = this.isTechnicalNonTenantPath(pathname)
            ? [fallbackCoreRoute]
            : (routeSegments.length === 0 || pathIsOnlyAuthenticatedTenant ? [fallbackCoreRoute] : routeSegments);

        return `/${encodeURIComponent(tenant)}/${pathWithoutTenant.join('/')}${suffix}`;
    }

    public tenantEnvelopeMatcher(segments: UrlSegment[]): UrlMatchResult | null {
        const routeSegments = segments.map(segment => decodeURIComponent(segment.path));
        if (!this.isTenantEnvelope(routeSegments)) {
            return null;
        }

        return {
            consumed: [segments[0]],
            posParams: {
                tenant: segments[0],
            },
        };
    }

    private extractRouteSegments(pathname: string): string[] {
        const normalizedPath = (pathname || '/').split('?')[0].split('#')[0].trim();
        return normalizedPath
            .split('/')
            .map(segment => segment.trim())
            .filter(segment => segment.length > 0)
            .map(segment => decodeURIComponent(segment));
    }

    private findSuffixStart(path: string): number {
        const queryIndex = path.indexOf('?');
        const hashIndex = path.indexOf('#');

        if (queryIndex === -1) {
            return hashIndex;
        }

        if (hashIndex === -1) {
            return queryIndex;
        }

        return Math.min(queryIndex, hashIndex);
    }

    private toAbsolutePath(pathname: string): string {
        if (!pathname || pathname === '/') {
            return '/';
        }

        return pathname.startsWith('/') ? pathname : `/${pathname}`;
    }
}